<script lang="ts" setup>
import { useEventListener } from '@/composeables';
import { useDebouncer } from '@/utilities/Debouncer';
import { elementOrComponentToElement, type ElementOrComponent } from '@/utilities/VueHelpers';
import { onMounted, reactive, ref, watch } from 'vue';

const props = withDefaults(
    defineProps<{
        parentElement: ElementOrComponent;
        isOpen?: boolean;
        position: 'TopLeft' | 'TopRight' | 'TopCenter' | 'BottomLeft' | 'BottomRight' | 'BottomCenter'; // TODO: Left | Right | Auto(?) | More?
    }>(),
    {
        isOpen: true,
    },
);

const style = reactive({
    top: 0,
    left: 0,
    transform: '',
    'z-index': 1070, // 1070 is the same as bs popover, seems like a sensible value here.
});

const popupElement = ref<HTMLElement | null>(null);

// Place if we change the parent or isOpen
watch([() => props.parentElement, () => props.isOpen], () => placePopup());

// Place if we scroll or resize the window
useEventListener('scroll', () => placePopup(), window, { capture: true });
useEventListener('resize', () => placePopup(), document, { capture: true });

// Observer for the size of the popup. This will detect v-if and display changes of the popup slots content
const resizeObserver = new ResizeObserver(() => placePopup());

// Watch the popupElement so we can maintain the resizeObserver on an element that may come in and out of the dom
watch(popupElement, (pop) => {
    resizeObserver.disconnect();
    if (!pop) {
        return;
    }
    resizeObserver.observe(pop);
});

// Place popup initially
onMounted(() => placePopup());

// Get a debouncer for debouncing the popup placement
const placeDebouncer = useDebouncer();

// Place the popup
const placePopup = () => {
    // We kinda get debouncing for free from vues tick stuff, but we can still debounce it ourselves
    // TODO: This makes scrolling kinda jagged.
    placeDebouncer(() => {
        if (!popupElement.value) {
            return;
        }

        // Find the dimensions of the popupElement. We use scrollWidth/Height since its absolutely positioned and doesnt have a boundingrect that makes sense
        const popupWidth = popupElement.value.scrollWidth;
        const popupHeight = popupElement.value.scrollHeight;

        // If we dont know the size of the child we can just exit
        if (!popupWidth || !popupHeight) {
            return;
        }

        // Turn the parent into a HTMLElement
        const parentElement = elementOrComponentToElement(props.parentElement);

        if (!parentElement) {
            return;
        }

        // Find out where the parent is in the page
        const parentBounding = parentElement.getBoundingClientRect();
        let transformX = parentBounding.x;
        let transformY = parentBounding.y;

        // TODO: This might be weirdness if the popup is wider than the parent
        switch (props.position) {
            case 'BottomLeft':
                transformY += parentBounding.height;
                break;
            case 'BottomRight':
                transformY += parentBounding.height;
                transformX += parentBounding.width - popupWidth;
                break;
            case 'BottomCenter':
                transformY += parentBounding.height;
                transformX += parentBounding.width / 2 - popupWidth / 2;
                break;
            case 'TopLeft':
                transformY -= popupHeight;
                break;
            case 'TopRight':
                transformY -= popupHeight;
                transformX += parentBounding.width - popupWidth;
                break;
            case 'TopCenter':
                transformY -= popupHeight;
                transformX += parentBounding.width / 2 - popupWidth / 2;
                break;
        }

        // Translate the popup to the parents location
        style.transform = `translate(${transformX}px, ${transformY}px)`;
    }, 5);
};
</script>
<template>
    <Teleport to="body">
        <div
            :style="style"
            class="position-absolute"
            v-if="isOpen"
            ref="popupElement"
        >
            <slot> </slot>
        </div>
    </Teleport>
</template>
